import type {
  PredefinedWallpaper,
  WallpaperAnimationResponse
} from 'shared/api/containerApi/requests/getAllDesktopSettings/getAllDesktopSettings.types'
import { getPredefinedWallpaperAnimation } from 'shared/api/containerApi/requests/getPredefinedWallpaperAnimation'
import { logger } from 'shared/services/logger'
import { create } from 'zustand'

import {
  animatedWallpaperDefinitionSchema,
  animatedWallpaperPreviewSchema
} from './animatedWallpaper.schema'
import type {
  CatalogAnimatedWallpaper,
  LoadedAnimatedWallpaper
} from './animatedWallpaper.types'

export const useAnimatedWallpaperCatalog = create<{
  wallpapers: CatalogAnimatedWallpaper[]
}>(() => ({ wallpapers: [] }))

// Responses from backends without preview support still carry full definitions.
const inlineDefinitions = new Map<string, unknown>()

const isValidAssetBaseUrl = (value: string) => {
  try {
    const url = new URL(value)
    return (
      ['https:', 'http:'].includes(url.protocol) &&
      !url.username &&
      !url.password &&
      !url.search &&
      !url.hash &&
      url.pathname.endsWith('/')
    )
  } catch {
    return false
  }
}

const withoutLayers = (definition: unknown): unknown => {
  if (!definition || typeof definition !== 'object') return definition
  const { layers: _layers, ...preview } = definition as Record<string, unknown>
  const seasons = preview.seasons
  if (!seasons || typeof seasons !== 'object') return preview
  return {
    ...preview,
    seasons: Object.fromEntries(
      Object.entries(seasons).map(([season, scene]) => [
        season,
        withoutLayers(scene)
      ])
    )
  }
}

const deliveryKey = (id: string, revision: string) => `${id}:${revision}`

export const setBackendAnimatedWallpapers = (
  records: PredefinedWallpaper[]
) => {
  const remote: CatalogAnimatedWallpaper[] = []
  inlineDefinitions.clear()
  for (const record of records) {
    if (record.kind !== 'animated' || !record.animation) continue
    const { preview, definition, asset_base_url, revision } = record.animation
    const parsed = animatedWallpaperPreviewSchema.safeParse(
      preview ?? withoutLayers(definition)
    )
    if (
      !parsed.success ||
      parsed.data.id !== record.wallpaper_id ||
      !isValidAssetBaseUrl(asset_base_url) ||
      !revision
    ) {
      logger.warn(`Skipping invalid backend animation ${record.wallpaper_id}`)
      continue
    }
    if (definition !== undefined)
      inlineDefinitions.set(deliveryKey(parsed.data.id, revision), definition)
    remote.push({
      ...parsed.data,
      name: record.name,
      assetBaseUrl: asset_base_url,
      revision
    })
  }
  useAnimatedWallpaperCatalog.setState({
    wallpapers: remote.sort(
      (a, b) => a.order - b.order || a.id.localeCompare(b.id)
    )
  })
}

export const getAnimatedWallpaper = (
  id: string
): CatalogAnimatedWallpaper | undefined =>
  useAnimatedWallpaperCatalog
    .getState()
    .wallpapers.find(wallpaper => wallpaper.id === id)

export class WallpaperRevisionChangedError extends Error {
  constructor(id: string) {
    super(`Wallpaper revision changed: ${id}`)
    this.name = 'WallpaperRevisionChangedError'
  }
}

export const loadAnimatedWallpaper = async (
  entry: CatalogAnimatedWallpaper
): Promise<LoadedAnimatedWallpaper> => {
  const inline = inlineDefinitions.get(deliveryKey(entry.id, entry.revision))
  const animation: WallpaperAnimationResponse =
    inline === undefined
      ? await getPredefinedWallpaperAnimation(entry.id)
      : {
          definition: inline,
          asset_base_url: entry.assetBaseUrl,
          revision: entry.revision
        }
  const parsed = animatedWallpaperDefinitionSchema.safeParse(
    animation.definition
  )
  if (
    !parsed.success ||
    parsed.data.id !== entry.id ||
    !isValidAssetBaseUrl(animation.asset_base_url) ||
    !animation.revision
  )
    throw new Error(`Invalid backend animation ${entry.id}`)
  // Never put a newer definition into the requested revision's query cache.
  if (animation.revision !== entry.revision)
    throw new WallpaperRevisionChangedError(entry.id)
  if (animation.asset_base_url !== entry.assetBaseUrl)
    throw new Error(`Inconsistent backend animation delivery ${entry.id}`)
  return {
    ...parsed.data,
    name: entry.name,
    assetBaseUrl: animation.asset_base_url,
    revision: animation.revision
  }
}
